/**
 * AffTrackLayer (#802)
 *
 * A drawn AFF aircraft track, registered with the LayerRegistry so it gets the
 * drawer's standard controls — visibility, opacity and z-order — instead of
 * living as an ad-hoc mapbox source outside the drawer entirely (#779).
 *
 * Rendering is unchanged from #779: one geojson source carrying the track line,
 * a blurred halo layer beneath a coloured line layer. The halo is what keeps a
 * track visible wherever the basemap matches its colour, so it is drawn first
 * and torn down last.
 *
 * These layers are EPHEMERAL: the config is flagged metadata.ephemeral so
 * buildPresetData skips them. A track id embeds Date.now() and could never
 * resolve again from a saved preset or a share link.
 */

import mapboxgl from 'mapbox-gl';
import { BaseLayer, LayerConfig } from './BaseLayer';
import { buildBreadcrumbPopupContent, buildTrackPopupContent } from '../liveLayers/providers/affTrackPopup';
import { forgetTrack } from '../liveLayers/providers/affTrackRegistry';
import { dispatchAffTrackRemoved } from '../../components/explorers/affExplorer/affTrackEvent';
import { liftedLineLayout, hasCompleteHeights, evenHeights } from '../liveLayers/providers/affLineElevation';
import { MODE_3D_EVENT } from '../mapcontrols/Map3DToggle/Map3DToggle';
import { isMap3D } from '../mapcontrols/Map3DToggle/map3DMode';
import {
  breadcrumbLayerSpecs,
  breadcrumbDotImage,
  hasBreadcrumbHeights,
  BREADCRUMB_ICON_ID,
} from './affTrackBreadcrumbLayers';
import { logger } from '../../utils/logger';

const log = logger('AffTrackLayer');

const TRACK_LINE_LAYOUT = { 'line-join': 'round', 'line-cap': 'round' } as const;

/** Whether a track carries heights at all — complete or not (#820 step 8). */
const carriesHeights = (data: any): boolean => Array.isArray(data?.properties?.elevation);

/**
 * The line source's copy of the track, its heights re-read at even distance
 * steps (#820). The line reads a height by line-progress, which counts
 * distance, while the backend sends one height per reported point — and a real
 * track's points bunch up while parked. The track's own data is not touched:
 * the breadcrumbs, the retime and the gap check all use the per-point heights.
 */
const lineSourceData = (data: any): any => {
  const coordinates = data?.geometry?.coordinates;
  const elevation = data?.properties?.elevation;
  if (data?.geometry?.type !== 'LineString' || !hasCompleteHeights(elevation)) return data;
  if (!Array.isArray(coordinates) || coordinates.length !== elevation.length) return data;

  return { ...data, properties: { ...data.properties, elevation: evenHeights(coordinates, elevation) } };
};
import {
  TRACK_LINE_WIDTH,
  TRACK_HALO_WIDTH,
  TRACK_HALO_BLUR,
  TRACK_HALO_OPACITY,
} from '../liveLayers/providers/affTrackColors';

export interface AffTrackLayerInput {
  trackId: string;
  aircraftName: string;
  data: GeoJSON.Feature | GeoJSON.FeatureCollection;
  color: string;
  halo: string;
}

/**
 * Build the LayerConfig for a drawn track. The drawer entry is named for the
 * aircraft, not for the track id — the id is machine detail.
 */
export function affTrackLayerConfig(input: AffTrackLayerInput): LayerConfig {
  const now = new Date().toISOString();
  return {
    id: input.trackId,
    name: input.aircraftName,
    type: 'geojson',
    enabled: true,
    category: 'overlays',
    layer_group: null,
    tags: ['aff', 'track'],
    source_url: null,
    legend_url: null,
    zoom_min: 0,
    zoom_max: 24,
    default_opacity: 1,
    default_order: 0,
    metadata: { ephemeral: true, aff: true },
    config: { data: input.data, color: input.color, halo: input.halo },
    created_at: now,
    updated_at: now,
  };
}

/**
 * One point feature per reported position (#812).
 *
 * The track's own geometry is a LineString, so the reports ride in
 * `properties.positions` (see the backend's affBreadcrumbs). A track from the
 * older /trackAc48h route carries none — it draws its line and no breadcrumbs
 * rather than failing on a missing property.
 *
 * `altM` is the altitude in metres, carried for lifting the dot in 3D (#820
 * step 8); `alt` stays the feet the popup shows.
 */
const breadcrumbFeatures = (data: any): GeoJSON.FeatureCollection => ({
  type: 'FeatureCollection',
  features: (data?.properties?.positions ?? []).map((p: any) => ({
    type: 'Feature',
    geometry: { type: 'Point', coordinates: [p.lon, p.lat] },
    properties: { alt: p.alt, altM: p.altM, speed: p.speed, heading: p.heading, t: p.t },
  })),
});

const hasBreadcrumbs = (data: any): boolean =>
  Array.isArray(data?.properties?.positions) && data.properties.positions.length > 0;

/** Every map layer id a drawn track owns. */
export const affTrackLayerIds = (trackId: string) => ({
  line: trackId,
  halo: `${trackId}-halo`,
  points: `${trackId}-points`,
  pointsHit: `${trackId}-points-hit`,
});

export class AffTrackLayer extends BaseLayer {
  private readonly sourceId: string;
  private readonly lineLayerId: string;
  private readonly haloLayerId: string;
  private readonly pointSourceId: string;
  private readonly pointLayerId: string;
  private readonly pointHitLayerId: string;
  /** Kept so they can be unbound; the layer ids are reused across retimes. */
  private breadcrumbHandlers: Array<[string, (e: any) => void]> = [];
  private breadcrumbPopup: mapboxgl.Popup | null = null;
  /**
   * Click and hover on the line, bound once per layer: a retime reuses the
   * layer id, so binding per draw stacked handlers (#866 M3).
   */
  private lineHandlers: Array<[string, (e: any) => void]> = [];
  private linePopup: mapboxgl.Popup | null = null;
  /**
   * Waits for terrain when the breadcrumbs had to be drawn flat in 3D for want
   * of it (#866 M6), then re-lays the track lifted. Bound only while waiting.
   */
  private terrainWaitHandler: (() => void) | null = null;
  /** Re-lays the line when the map changes mode; only bound for a track with heights (#820). */
  private modeHandler: (() => void) | null = null;

  constructor(map: mapboxgl.Map, config: LayerConfig) {
    super(map, config);
    const ids = affTrackLayerIds(config.id);
    this.sourceId = config.id;
    this.lineLayerId = ids.line;
    this.haloLayerId = ids.halo;
    this.pointSourceId = ids.points;
    this.pointLayerId = ids.points;
    this.pointHitLayerId = ids.pointsHit;
  }

  protected async onInitialize(): Promise<void> {
    // Nothing to fetch: the track geometry arrives already resolved in config.
  }

  protected async onEnable(): Promise<void> {
    const { data } = this.config.config;

    if (!this.map.getSource(this.sourceId)) {
      // #820 step 8 — line metrics make line-progress available, which lifting
      // the track point by point depends on. Only a track that carries heights
      // can be lifted, so only it pays for them.
      this.map.addSource(
        this.sourceId,
        carriesHeights(data)
          ? { type: 'geojson', data: lineSourceData(data), lineMetrics: true }
          : { type: 'geojson', data },
      );
    }

    this.addLineLayers();
    this.bindLineInteraction();

    // A track without heights or breadcrumbs never changes with the mode, so it
    // has nothing to listen for.
    if ((carriesHeights(data) || hasBreadcrumbs(data)) && !this.modeHandler) {
      this.modeHandler = () => this.relayTrack();
      this.map.on(MODE_3D_EVENT as any, this.modeHandler);
    }

    // Breadcrumbs last, so they sit above the line and take the click (#812).
    if (hasBreadcrumbs(data)) {
      if (!this.map.getSource(this.pointSourceId)) {
        this.map.addSource(this.pointSourceId, {
          type: 'geojson',
          data: breadcrumbFeatures(data),
        });
      }

      this.addBreadcrumbLayers();
      this.bindBreadcrumbInteraction();
    }
  }

  /**
   * How to lift the breadcrumbs right now (#820 step 8), or null to draw them
   * flat: flat in 2D, and flat in 3D if any breadcrumb has no altitude in metres.
   *
   * The exaggeration is needed because symbol offsets are in RENDERED metres,
   * unlike the lines (see affTrackBreadcrumbLayers). A 3D map with no terrain
   * has no exaggeration to give, and guessing one would lift every dot to the
   * wrong height and look right — so that fails loudly, as the models do.
   */
  private breadcrumbLift(): { exaggeration: number } | null {
    if (!isMap3D(this.map)) return null;

    const positions = this.config.config.data?.properties?.positions ?? [];
    if (!hasBreadcrumbHeights(positions)) {
      log.warn(
        `AFF track for ${this.config.name} has a breadcrumb with no altitude; its breadcrumbs are drawn on the ground in 3D`,
      );
      return null;
    }

    const terrain = this.map.getTerrain();
    if (!terrain) {
      // #866 M6 — between a basemap's setStyle and its style.load the map has no
      // terrain. Throwing here threw out of the 3D toggle's map.fire, and in a
      // retime left the track half removed. Guessing an exaggeration would lift
      // every dot to the wrong height and look right, so they are drawn flat
      // for now, named, and lifted once terrain is back.
      log.warn(
        `AFF track breadcrumbs for ${this.config.name} are drawn flat until the map has terrain to lift them`,
      );
      this.waitForTerrain();
      return null;
    }
    return { exaggeration: terrain.exaggeration as number };
  }

  private waitForTerrain(): void {
    if (this.terrainWaitHandler) return;
    this.terrainWaitHandler = () => {
      if (!this.map.getTerrain()) return;
      this.stopWaitingForTerrain();
      if (isMap3D(this.map)) this.relayTrack();
    };
    this.map.on('idle', this.terrainWaitHandler);
  }

  private stopWaitingForTerrain(): void {
    if (!this.terrainWaitHandler) return;
    this.map.off('idle', this.terrainWaitHandler);
    this.terrainWaitHandler = null;
  }

  /**
   * Add the breadcrumb dots and their invisible hover target, lifted or flat for
   * the current mode.
   *
   * @param beforeId where to insert them, so a re-lay keeps the track in its
   *   place among the other drawer layers
   * @param lift decided by the caller when it must be known before anything is
   *   removed (relayTrack)
   */
  private addBreadcrumbLayers(beforeId?: string, lift = this.breadcrumbLift()): void {
    const { color, halo } = this.config.config;

    // The lifted dots are drawn from one shared SDF image, tinted per track. A
    // basemap change drops registered images with the style, so check each time.
    if (lift && !this.map.hasImage(BREADCRUMB_ICON_ID)) {
      this.map.addImage(BREADCRUMB_ICON_ID, breadcrumbDotImage(), { sdf: true });
    }

    const specs = breadcrumbLayerSpecs({
      sourceId: this.pointSourceId,
      pointLayerId: this.pointLayerId,
      hitLayerId: this.pointHitLayerId,
      color,
      halo,
      opacity: this.getOpacity(),
      lift,
    });

    for (const spec of specs) {
      if (this.map.getLayer(spec.id)) continue;
      if (beforeId) this.map.addLayer(spec as any, beforeId);
      else this.map.addLayer(spec as any);
    }
  }

  /**
   * Whether the line should be lifted right now (#820 step 8): the map is in 3D
   * and every point has a real height. Decided once per lay so a gap is named
   * once, not once per layer.
   */
  private shouldLift(): boolean {
    const { data } = this.config.config;
    if (!isMap3D(this.map) || !carriesHeights(data)) return false;
    if (hasCompleteHeights(data.properties.elevation)) return true;

    // Decided 2026-09-11 for models, as for tails: no altitude means drawn on
    // the ground, and the gap made visible.
    log.warn(
      `AFF track for ${this.config.name} has a position with no altitude; it is drawn on the ground in 3D`,
    );
    return false;
  }

  /**
   * Add the halo and the line, lifted or flat for the current mode. Halo first
   * so it sits beneath the line.
   *
   * @param beforeId where to insert them; used on a re-lay to keep them beneath
   *   the breadcrumbs, since a layer added without it lands on top of the map
   */
  private addLineLayers(beforeId?: string, lifted = this.shouldLift()): void {
    const { color, halo } = this.config.config;
    const layout = lifted ? liftedLineLayout(TRACK_LINE_LAYOUT) : { ...TRACK_LINE_LAYOUT };

    if (!this.map.getLayer(this.haloLayerId)) {
      this.map.addLayer(
        {
          id: this.haloLayerId,
          type: 'line',
          source: this.sourceId,
          layout,
          paint: {
            'line-color': halo,
            'line-width': TRACK_HALO_WIDTH,
            'line-blur': TRACK_HALO_BLUR,
            'line-opacity': TRACK_HALO_OPACITY * this.getOpacity(),
          },
        } as any,
        beforeId,
      );
    }

    if (!this.map.getLayer(this.lineLayerId)) {
      this.map.addLayer(
        {
          id: this.lineLayerId,
          type: 'line',
          source: this.sourceId,
          layout,
          paint: {
            'line-color': color,
            'line-width': TRACK_LINE_WIDTH,
            'line-opacity': this.getOpacity(),
          },
        } as any,
        beforeId,
      );
    }
  }

  /**
   * The layer directly above this track's own layers, if any — where a re-lay
   * has to put them back so the track keeps its place among the drawer's other
   * layers. A layer added without a beforeId lands on top of the whole map.
   */
  private layerAboveTrack(): string | undefined {
    const own = new Set([this.haloLayerId, this.lineLayerId, this.pointLayerId, this.pointHitLayerId]);
    const ids = (this.map.getStyle?.()?.layers ?? []).map((l: { id: string }) => l.id);

    let lastOwn = -1;
    ids.forEach((id: string, i: number) => {
      if (own.has(id)) lastOwn = i;
    });
    if (lastOwn < 0) return undefined;

    return ids.slice(lastOwn + 1).find((id: string) => !own.has(id));
  }

  /**
   * Put the track back for the current mode (#820 step 8): halo and line lifted
   * or flat, breadcrumbs as lifted symbols or flat circles.
   *
   * Re-added rather than switched in place: whether line-elevation-reference can
   * be changed on a live layer is unverified, and a circle cannot be turned into
   * a symbol at all. Everything goes back beneath whatever was above the track,
   * in its own order — halo, line, dots, hover target — so the dots stay over
   * the line and keep the pointer, and the track keeps its place in the drawer.
   * The opacity carries over because the new layers are built from getOpacity(),
   * and the hover handlers survive because they are bound by layer id, which is
   * reused (checked in the browser 2026-09-13).
   */
  private relayTrack(): void {
    const beforeId = this.layerAboveTrack();
    // Everything that decides the new layers is worked out before any layer
    // is removed (#866 M6), so nothing can fail with the track half taken off.
    const hasPoints = !!this.map.getSource(this.pointSourceId);
    const lifted = this.shouldLift();
    const lift = hasPoints ? this.breadcrumbLift() : null;

    for (const id of [this.pointHitLayerId, this.pointLayerId, this.lineLayerId, this.haloLayerId]) {
      if (this.map.getLayer(id)) this.map.removeLayer(id);
    }

    this.addLineLayers(beforeId, lifted);
    if (hasPoints) this.addBreadcrumbLayers(beforeId, lift);
  }

  /**
   * Show what one reported position carried (#812).
   *
   * On hover rather than on click: the dot is 3.5px, and asking someone to hit
   * it exactly — on a map they may also be panning — makes the detail
   * effectively unreachable. Click stays bound because a touch screen has no
   * hover at all, and the field tablets would otherwise lose it entirely.
   *
   * Bound per track rather than once for all of them: the handlers belong to
   * this track's own layers, and one track can be removed while others stay.
   */
  private bindBreadcrumbInteraction(): void {
    if (this.breadcrumbHandlers.length) return;

    const show = (e: any) => {
      const props = e?.features?.[0]?.properties;
      if (!props) return;

      // One popup at a time. Sweeping along a dense track would otherwise
      // leave a trail of them behind the pointer.
      this.breadcrumbPopup?.remove();
      this.breadcrumbPopup = new mapboxgl.Popup({ closeButton: false, offset: 10 })
        .setLngLat(e.lngLat)
        .setDOMContent(
          buildBreadcrumbPopupContent({
            alt: props.alt ?? null,
            speed: props.speed ?? null,
            heading: props.heading ?? null,
            t: props.t ?? null,
          }),
        )
        .addTo(this.map);
    };

    const hide = () => {
      this.breadcrumbPopup?.remove();
      this.breadcrumbPopup = null;
    };

    const enter = (e: any) => {
      this.map.getCanvas().style.cursor = 'pointer';
      show(e);
    };

    const leave = () => {
      this.map.getCanvas().style.cursor = '';
      hide();
    };

    this.breadcrumbHandlers = [
      ['mouseenter', enter],
      ['mouseleave', leave],
      ['click', show],
    ];

    for (const [event, handler] of this.breadcrumbHandlers) {
      this.map.on(event as any, this.pointHitLayerId, handler);
    }
  }

  /**
   * Popup on click: which aircraft, over what window, and a way to take the
   * track back off the map. Built as DOM so the dismiss button can carry a
   * handler and the esn stays text.
   *
   * Idempotent: a retime or a basemap change re-enables this same layer
   * (forceReEnable), and must not bind a second set.
   */
  private bindLineInteraction(): void {
    if (this.lineHandlers.length) return;

    const click = (e: any) => {
      const feature = e?.features?.[0];
      if (!feature) return;
      // A breadcrumb sits on its line, so one click reaches both layers. The dot
      // is the more specific target and opens its own popup; the line stands
      // down rather than open a second one over it (#866).
      if (
        this.map.getLayer(this.pointHitLayerId) &&
        this.map.queryRenderedFeatures(e.point, { layers: [this.pointHitLayerId] }).length > 0
      ) {
        return;
      }

      this.linePopup?.remove();
      const popup = new mapboxgl.Popup().setLngLat(e.lngLat);
      popup.setDOMContent(
        buildTrackPopupContent({
          esn: String(feature.properties?.name ?? this.config.metadata?.esn ?? this.config.name),
          // The window actually drawn, carried on the feature.
          hours: Number(feature.properties?.hours ?? this.config.metadata?.span?.hours ?? 48),
          pointCount: Number(feature.properties?.pointCount ?? 0),
          onDismiss: () => {
            // The drawer destroys this layer, which unbinds these handlers (#802).
            dispatchAffTrackRemoved({ trackId: this.config.id });
            forgetTrack(this.config.id);
            popup.remove();
          },
        }),
      );
      popup.addTo(this.map);
      this.linePopup = popup;
    };
    const enter = () => { this.map.getCanvas().style.cursor = 'pointer'; };
    const leave = () => { this.map.getCanvas().style.cursor = ''; };

    this.lineHandlers = [
      ['click', click],
      ['mouseenter', enter],
      ['mouseleave', leave],
    ];
    for (const [event, handler] of this.lineHandlers) {
      this.map.on(event as any, this.lineLayerId, handler);
    }
  }

  /**
   * Replace the track geometry in place (#802 AC7).
   *
   * A timeframe change redraws one track over a new range. Re-running the draw
   * would mint a new track-<esn>-<Date.now()> id and leave a second drawer
   * entry behind, so the source is updated and the config kept in step — the
   * config is what a re-enable re-adds from after a basemap change.
   */
  public setData(data: GeoJSON.Feature | GeoJSON.FeatureCollection): void {
    this.config.config.data = data;

    const source = this.map.getSource(this.sourceId) as mapboxgl.GeoJSONSource | undefined;
    source?.setData(lineSourceData(data));

    // The breadcrumbs describe the span, so they follow it (#812). A retime to
    // a range with no reports empties them rather than leaving the old ones.
    const pointSource = this.map.getSource(this.pointSourceId) as mapboxgl.GeoJSONSource | undefined;
    pointSource?.setData(breadcrumbFeatures(data) as any);

    // #820 step 8 — the new span may have a gap in its heights where the old
    // one had none. Re-decide, rather than leave a lifted line interpolating
    // over a null.
    // Only in 3D: in 2D nothing a retime changes can alter how the track is
    // drawn — lines are flat and breadcrumbs are circles whatever the heights.
    if (this.modeHandler && isMap3D(this.map)) this.relayTrack();
  }

  protected async onDisable(): Promise<void> {
    // Unbind before the layers go. The ids are reused when a track is retimed,
    // so handlers left behind would fire twice for one pointer.
    for (const [event, handler] of this.breadcrumbHandlers) {
      this.map.off(event as any, this.pointHitLayerId, handler);
    }
    this.breadcrumbHandlers = [];
    this.breadcrumbPopup?.remove();
    this.breadcrumbPopup = null;

    for (const [event, handler] of this.lineHandlers) {
      this.map.off(event as any, this.lineLayerId, handler);
    }
    this.lineHandlers = [];
    this.linePopup?.remove();
    this.linePopup = null;

    this.stopWaitingForTerrain();

    // A removed track left listening would re-add its layers on the next switch.
    if (this.modeHandler) {
      this.map.off(MODE_3D_EVENT as any, this.modeHandler);
      this.modeHandler = null;
    }

    // Layers before the source: removing a source still referenced by a layer
    // throws in mapbox.
    for (const id of [this.pointHitLayerId, this.pointLayerId, this.lineLayerId, this.haloLayerId]) {
      if (this.map.getLayer(id)) this.map.removeLayer(id);
    }
    for (const id of [this.pointSourceId, this.sourceId]) {
      if (this.map.getSource(id)) this.map.removeSource(id);
    }
  }

  protected async onSetOpacity(opacity: number): Promise<void> {
    // The halo carries its own baseline transparency; scale it rather than
    // overwrite it, or fading a track turns the halo into a hard band.
    if (this.map.getLayer(this.haloLayerId)) {
      this.map.setPaintProperty(this.haloLayerId, 'line-opacity', TRACK_HALO_OPACITY * opacity);
    }
    if (this.map.getLayer(this.lineLayerId)) {
      this.map.setPaintProperty(this.lineLayerId, 'line-opacity', opacity);
    }
    // Breadcrumbs fade with their track, fill and outline together — otherwise
    // a faded track keeps a row of hard dots along it (#812). Lifted in 3D they
    // are symbols (#820), and a circle property sent to a symbol layer is an
    // error, so the property follows the layer's current type.
    const points = this.map.getLayer(this.pointLayerId) as { type?: string } | undefined;
    if (points?.type === 'symbol') {
      this.map.setPaintProperty(this.pointLayerId, 'icon-opacity', opacity);
    } else if (points) {
      this.map.setPaintProperty(this.pointLayerId, 'circle-opacity', opacity);
      this.map.setPaintProperty(this.pointLayerId, 'circle-stroke-opacity', opacity);
    }
  }

  protected onSetOrder(): void {
    // Order is applied by the registry's reorderAllLayers, which calls
    // map.moveLayer over getMapboxLayerIds(). Nothing to do per-layer.
  }

  protected async onDestroy(): Promise<void> {
    await this.onDisable();
  }

  /**
   * Halo first so the set keeps its relative stacking through a reorder, with
   * the breadcrumbs on top where they stay visible and clickable (#812).
   */
  public getMapboxLayerIds(): string[] {
    return [this.haloLayerId, this.lineLayerId, this.pointLayerId, this.pointHitLayerId];
  }

  public getMapboxSourceIds(): string[] {
    return [this.sourceId, this.pointSourceId];
  }
}
